import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SearchPicker } from "@/components/SearchPicker";
import { BilledToFields } from "@/components/BilledToFields";
import { api, errorMessage } from "@/lib/api";
import { ITEM_KINDS, addDays, money, todayISO } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const blankItem = (kind = "appearance") => ({
  item_id: `li_${Math.random().toString(36).slice(2, 12)}`,
  kind,
  description: "",
  detail: "",
  quantity: 1,
  rate: 0,
  taxable: kind !== "disbursement",
});

const PARTY_OPTIONS = [
  { value: "both", label: "Billing party & client" },
  { value: "billing", label: "Billing party only" },
  { value: "client", label: "Client only" },
];

const BLANK_BLOCK = {
  name: "",
  designation: "",
  org: "",
  address: "",
  city: "",
  email: "",
  phone: "",
  tax_id: "",
};

const BLANK_ADHOC = { matter_title: "", forum: "", matter_ref: "", client_name: "", party_role: "" };

export default function InvoiceBuilder() {
  const { invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { settings, currency } = useAuth();
  const taxOn = settings?.tax_enabled !== false;
  const taxLabel = settings?.tax_label || "Tax";

  const [cases, setCases] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [caseQuery, setCaseQuery] = useState("");
  const [caseOpen, setCaseOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("matter");
  const [blocks, setBlocks] = useState([]);
  const [pastAdhoc, setPastAdhoc] = useState([]);
  const [copiedFrom, setCopiedFrom] = useState("");
  const [billTo, setBillTo] = useState("contact");
  const [form, setForm] = useState({
    case_id: searchParams.get("case") || "",
    contact_id: "",
    issue_date: todayISO(),
    due_date: "",
    tax_rate: 0,
    discount: 0,
    notes: "",
    status: "draft",
    party_display: "both",
    billed_to: BLANK_BLOCK,
    remember_billed_to: false,
    adhoc: BLANK_ADHOC,
    items: [blankItem()],
  });

  useEffect(() => {
    (async () => {
      try {
        const [caseRes, contactRes] = await Promise.all([
          api.get("/cases"),
          api.get("/contacts"),
        ]);
        setCases(caseRes.data);
        setContacts(contactRes.data);
        setPastAdhoc([]);
        api
          .get("/invoices")
          .then(({ data }) => setPastAdhoc((data || []).filter((r) => r.is_adhoc)))
          .catch(() => setPastAdhoc([]));

        const copyId = searchParams.get("copy");
        if (invoiceId) {
          const { data } = await api.get(`/invoices/${invoiceId}`);
          const inv = data.invoice;
          setMode(inv.is_adhoc ? "adhoc" : "matter");
          setBillTo(inv.billed_to?.name ? "custom" : "contact");
          setForm({
            case_id: inv.case_id || "",
            contact_id: inv.contact_id || "",
            issue_date: inv.issue_date,
            due_date: inv.due_date || "",
            tax_rate: inv.tax_rate,
            discount: inv.discount,
            notes: inv.notes || "",
            status: inv.status,
            party_display: inv.party_display || settings?.default_party_display || "both",
            billed_to: { ...BLANK_BLOCK, ...(inv.billed_to || {}) },
            remember_billed_to: false,
            adhoc: { ...BLANK_ADHOC, ...(inv.adhoc || {}) },
            items: inv.items.length ? inv.items : [blankItem()],
          });
        } else {
          const preset = searchParams.get("case") || "";
          const chosen = caseRes.data.find((c) => c.case_id === preset);
          setForm((f) => ({
            ...f,
            case_id: preset,
            contact_id: chosen?.contact_id || "",
            due_date: addDays(todayISO(), settings?.payment_terms_days ?? 15),
            tax_rate: taxOn ? settings?.default_tax_rate ?? 18 : 0,
            party_display: settings?.default_party_display || "both",
          }));
          if (copyId) await copyFrom(copyId);
        }
      } catch (error) {
        toast.error(errorMessage(error, "Could not load the builder"));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  const selectedCase = cases.find((c) => c.case_id === form.case_id);
  const selectedContact = contacts.find((c) => c.contact_id === form.contact_id);

  const caseLabel = (c) =>
    [c.title, c.matter_ref, c.client_name].filter(Boolean).join(" · ");

  const matches = (() => {
    const needle = caseQuery.trim().toLowerCase();
    if (!needle) return cases;
    return cases.filter((c) =>
      [c.title, c.matter_ref, c.forum, c.client_name, c.contact_name, c.represented_party]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle)),
    );
  })();

  const briefContacts = selectedCase?.contacts || [];  const orgOf = (c) => c.firm_name || c.client_name || "Direct client";
  const contactOptions = [
    ...briefContacts.map((p) => ({
      contact_id: p.contact_id,
      full_name: p.full_name,
      org: p.org_name || "Direct client",
      onBrief: true,
    })),
    ...contacts
      .filter((c) => !briefContacts.some((p) => p.contact_id === c.contact_id))
      .map((c) => ({
        contact_id: c.contact_id,
        full_name: c.full_name,
        org: orgOf(c),
        onBrief: false,
      })),
  ];

  const onCaseChange = (value) => {
    const chosen = cases.find((c) => c.case_id === value);
    setForm((f) => ({
      ...f,
      case_id: value,
      contact_id: chosen?.contact_id || f.contact_id,
    }));
    setCaseQuery("");
    setCaseOpen(false);
  };

  const clientId = selectedCase?.client_id || "";

  useEffect(() => {
    if (!clientId) {
      setBlocks([]);
      return;
    }
    api
      .get(`/clients/${clientId}/billing-blocks`)
      .then(({ data }) => setBlocks(data || []))
      .catch(() => setBlocks([]));
  }, [clientId]);

  const billToOptions = [
    {
      id: "contact",
      label: "The contact person's own details",
      hint: selectedContact?.full_name || "As held on record",
    },
    ...blocks.map((b) => ({
      id: b.block_id,
      label: b.name,
      hint: [b.designation, b.org, b.city].filter(Boolean).join(" · ") || "Saved block",
    })),
    { id: "custom", label: "Type a custom billed-to block…", hint: "One-off details" },
  ];

  const onBillToChange = (value) => {
    setBillTo(value || "contact");
    const block = blocks.find((b) => b.block_id === value);
    if (block) {
      const { block_id: _id, created_at: _at, ...rest } = block;
      setForm((f) => ({ ...f, billed_to: { ...BLANK_BLOCK, ...rest }, remember_billed_to: false }));
    } else if (value === "contact") {
      setForm((f) => ({ ...f, billed_to: BLANK_BLOCK, remember_billed_to: false }));
    }
  };

  const setBlockField = (patch) =>
    setForm((f) => ({ ...f, billed_to: { ...f.billed_to, ...patch } }));

  const copyFrom = async (sourceId) => {
    try {
      const { data } = await api.get(`/invoices/${sourceId}`);
      const src = data.invoice;
      if (!src.is_adhoc) {
        toast.error("Only one-off fee notes can be copied");
        return;
      }
      setMode("adhoc");
      setBillTo("custom");
      setForm((f) => ({
        ...f,
        case_id: "",
        contact_id: src.contact_id || "",
        issue_date: todayISO(),
        due_date: addDays(todayISO(), settings?.payment_terms_days ?? 15),
        tax_rate: src.tax_rate,
        discount: src.discount,
        notes: src.notes || "",
        status: "draft",
        party_display: src.party_display || "both",
        billed_to: { ...BLANK_BLOCK, ...(src.billed_to || {}) },
        remember_billed_to: false,
        adhoc: { ...BLANK_ADHOC, ...(src.adhoc || {}) },
        items: src.items?.length ? src.items.map((i) => ({ ...i })) : [blankItem()],
      }));
      setCopiedFrom(src.invoice_number);
      toast.success(`Copied from ${src.invoice_number} — nothing is saved until you press save`);
    } catch (error) {
      toast.error(errorMessage(error, "Could not copy that fee note"));
    }
  };

  const onAdhocBillToChange = (value) => {
    if (!value || value === "custom") {
      setForm((f) => ({ ...f, contact_id: "" }));
      return;
    }
    const chosen = contacts.find((c) => c.contact_id === value);
    if (!chosen) return;
    setForm((f) => ({
      ...f,
      contact_id: value,
      billed_to: {
        ...BLANK_BLOCK,
        name: chosen.full_name || "",
        designation: chosen.designation || "",
        org: chosen.firm_name || chosen.client_name || "",
        address: chosen.address || "",
        email: chosen.email || "",
        phone: chosen.whatsapp || "",
      },
    }));
  };

  const setAdhocField = (patch) => setForm((f) => ({ ...f, adhoc: { ...f.adhoc, ...patch } }));

  const setItem = (index, patch) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    }));

  const totals = useMemo(() => {
    let fees = 0;
    let disb = 0;
    let taxable = 0;
    form.items.forEach((item) => {
      const amount = Number(item.quantity || 0) * Number(item.rate || 0);
      if (item.kind === "disbursement") disb += amount;
      else fees += amount;
      if (item.taxable) taxable += amount;
    });
    const subtotal = fees + disb;
    const discount = Number(form.discount || 0);
    const base = Math.max(taxable - discount, 0);
    const rate = taxOn ? Number(form.tax_rate || 0) : 0;
    const tax = (base * rate) / 100;
    return { fees, disb, subtotal, discount, tax, total: subtotal - discount + tax };
  }, [form.items, form.discount, form.tax_rate, taxOn]);

  const submit = useCallback(
    async (status) => {
      const adhocMode = mode === "adhoc";
      if (adhocMode) {
        if (!form.adhoc.matter_title.trim()) {
          toast.error("Describe what this one-off fee note is for");
          return;
        }
        if (!form.billed_to.name.trim()) {
          toast.error("Type who this fee note is billed to");
          return;
        }
      } else {
        if (!form.case_id) {
          toast.error("Select the matter being billed");
          return;
        }
        if (!form.contact_id) {
          toast.error("Select the instructing contact to bill");
          return;
        }
        if (billTo !== "contact" && !form.billed_to.name.trim()) {
          toast.error("Type a name for the custom billed-to block");
          return;
        }
      }
      const items = form.items.filter(
        (i) => (i.description || "").trim() || Number(i.rate || 0) > 0,
      );
      if (items.length === 0) {
        toast.error("Add at least one fee or disbursement line");
        return;
      }
      setSaving(true);
      const payload = {
        case_id: adhocMode ? null : form.case_id,
        contact_id: adhocMode ? form.contact_id || null : form.contact_id,
        issue_date: form.issue_date,
        due_date: form.due_date || null,
        items: items.map((i) => ({
          ...i,
          quantity: Number(i.quantity || 0),
          rate: Number(i.rate || 0),
        })),
        tax_rate: taxOn ? Number(form.tax_rate || 0) : 0,
        discount: Number(form.discount || 0),
        notes: form.notes,
        status,
        party_display: form.party_display,
        is_adhoc: adhocMode,
        adhoc: adhocMode ? form.adhoc : null,
        billed_to: adhocMode || billTo !== "contact" ? form.billed_to : null,
        remember_billed_to: !adhocMode && billTo === "custom" && !!form.remember_billed_to,
      };
      try {
        const { data } = invoiceId
          ? await api.put(`/invoices/${invoiceId}`, payload)
          : await api.post("/invoices", payload);
        toast.success(invoiceId ? "Invoice updated" : `Invoice ${data.invoice_number} created`);
        navigate(`/invoices/${data.invoice_id}`, { replace: true });
      } catch (error) {
        toast.error(errorMessage(error, "Could not save invoice"));
      } finally {
        setSaving(false);
      }
    },
    [form, invoiceId, navigate, taxOn, mode, billTo],
  );

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center" data-testid="builder-loading">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  const clientName = selectedCase?.client_name || selectedCase?.represented_party || "the client";
  const partyHint = {
    both: `Fee note reads "Billed to ${
      selectedContact?.full_name || "the instructing lawyer"
    }" with a CLIENT column beside it carrying ${clientName}'s address, GST number and contact details.`,
    billing: `Fee note is addressed to ${
      selectedContact?.full_name || "the instructing lawyer"
    } alone — the client is not named anywhere on it.`,
    client: `Fee note is addressed to ${clientName} with their own address and GST number — the instructing lawyer appears only as "Instructed by".`,
  }[form.party_display || "both"];

  return (
    <div className="space-y-8" data-testid="invoice-builder-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate(-1)}
            data-testid="builder-back-button"
            className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 transition-colors duration-150 hover:text-slate-900"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.8} />
            Back
          </button>
          <h1 className="mt-4 font-serif text-3xl tracking-tight text-slate-900 lg:text-4xl">
            {invoiceId ? "Edit fee note" : "Raise a fee note"}
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Appearance fees, hourly engagement, drafting and out-of-pocket disbursements on one
            statement.
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <section className="surface p-6" data-testid="invoice-header-section">
            <h2 className="font-serif text-xl text-slate-900">
              {mode === "adhoc" ? "One-off fee note" : "Matter & billing party"}
            </h2>
            <Tabs value={mode} onValueChange={setMode} className="mt-4">
              <TabsList className="h-auto flex-wrap justify-start gap-1 rounded-sm">
                <TabsTrigger
                  value="matter"
                  className="rounded-sm"
                  disabled={!!invoiceId}
                  data-testid="builder-mode-matter"
                >
                  Against a matter
                </TabsTrigger>
                <TabsTrigger
                  value="adhoc"
                  className="rounded-sm"
                  disabled={!!invoiceId}
                  data-testid="builder-mode-adhoc"
                >
                  One-off, no matter
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              {mode === "adhoc" && (
                <>
                  {pastAdhoc.length > 0 && !invoiceId && (
                    <div className="space-y-2 sm:col-span-2">
                      <Label className="label-caps">Start from an earlier one-off fee note</Label>
                      <SearchPicker
                        items={pastAdhoc.map((r) => ({
                          id: r.invoice_id,
                          label: `${r.invoice_number} — ${r.case_title}`,
                          hint: [r.contact_name, r.issue_date].filter(Boolean).join(" · "),
                        }))}
                        value=""
                        onSelect={(id) => id && copyFrom(id)}
                        placeholder="Copy the particulars from a past one-off…"
                        testId="adhoc-copy"
                        emptyLabel="No earlier one-off fee note matches that text."
                      />
                      {copiedFrom ? (
                        <p className="text-[11px] text-slate-500" data-testid="adhoc-copied-from">
                          Copied from {copiedFrom}. Edit anything below — a new number is issued when
                          you save.
                        </p>
                      ) : null}
                    </div>
                  )}
                  {mode === "adhoc" && (
                    <div className="space-y-2 sm:col-span-2">
                      <Label className="label-caps">Bill to</Label>
                      <SearchPicker
                        items={[
                          { id: "custom", label: "Type one-time details below", hint: "Not saved anywhere" },
                          ...contacts.map((c) => ({
                            id: c.contact_id,
                            label: c.full_name,
                            hint: [c.firm_name || c.client_name || "Direct client", c.designation]
                              .filter(Boolean)
                              .join(" · "),
                          })),
                        ]}
                        value={form.contact_id || "custom"}
                        onSelect={onAdhocBillToChange}
                        placeholder="Type one-time details below"
                        testId="adhoc-billto"
                        emptyLabel="No instructing contact matches that text."
                      />
                      <p className="text-[11px] text-slate-500">
                        {form.contact_id
                          ? "Billed to a contact on record — this fee note will also appear on their account page, statements and portal."
                          : "One-time details only — this fee note stays out of every account page and statement."}
                      </p>
                    </div>
                  )}
                  <div className="space-y-2 sm:col-span-2">
                    <Label className="label-caps">What is this fee note for? *</Label>
                    <Input
                      value={form.adhoc.matter_title}
                      onChange={(e) => setAdhocField({ matter_title: e.target.value })}
                      placeholder="Opinion on the arbitration clause / conference at chambers"
                      className="rounded-sm"
                      data-testid="adhoc-title"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Forum / court</Label>
                    <Input
                      value={form.adhoc.forum}
                      onChange={(e) => setAdhocField({ forum: e.target.value })}
                      className="rounded-sm"
                      data-testid="adhoc-forum"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Reference</Label>
                    <Input
                      value={form.adhoc.matter_ref}
                      onChange={(e) => setAdhocField({ matter_ref: e.target.value })}
                      className="rounded-sm"
                      data-testid="adhoc-ref"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Client / party name</Label>
                    <Input
                      value={form.adhoc.client_name}
                      onChange={(e) => setAdhocField({ client_name: e.target.value })}
                      className="rounded-sm"
                      data-testid="adhoc-client-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Party role</Label>
                    <Input
                      value={form.adhoc.party_role}
                      onChange={(e) => setAdhocField({ party_role: e.target.value })}
                      placeholder="Petitioner / Respondent"
                      className="rounded-sm"
                      data-testid="adhoc-party-role"
                    />
                  </div>
                  <p
                    className="text-[11px] leading-relaxed text-slate-500 sm:col-span-2"
                    data-testid="adhoc-hint"
                  >
                    None of this is added to your matters, clients or contacts — it lives on this fee
                    note alone. It is numbered in its own series.
                  </p>
                </>
              )}
              {mode === "matter" && (
              <>
              <div className="relative space-y-2 sm:col-span-2">
                <Label className="label-caps">Matter / case *</Label>
                <Input
                  value={caseOpen ? caseQuery : selectedCase ? caseLabel(selectedCase) : ""}
                  onChange={(e) => {
                    setCaseQuery(e.target.value);
                    setCaseOpen(true);
                  }}
                  onFocus={() => {
                    setCaseQuery("");
                    setCaseOpen(true);
                  }}
                  onBlur={() => window.setTimeout(() => setCaseOpen(false), 150)}
                  placeholder="Start typing a case title, reference, court or client…"
                  autoComplete="off"
                  className="rounded-sm"
                  data-testid="builder-case-search"
                />
                {caseOpen && (
                  <div
                    className="absolute inset-x-0 top-[70px] z-30 max-h-60 overflow-y-auto border border-slate-300 bg-white shadow-lg"
                    data-testid="builder-case-options"
                  >
                    {matches.length === 0 ? (
                      <p className="px-4 py-3 text-[12px] text-slate-500">
                        No matter matches that text.
                      </p>
                    ) : (
                      matches.map((c) => (
                        <button
                          key={c.case_id}
                          type="button"
                          onMouseDown={(event) => {
                            event.preventDefault();
                            onCaseChange(c.case_id);
                          }}
                          data-testid={`builder-case-option-${c.case_id}`}
                          className={`block w-full px-4 py-2.5 text-left transition-colors duration-150 hover:bg-slate-50 ${
                            c.case_id === form.case_id ? "bg-slate-50" : ""
                          }`}
                        >
                          <span className="block truncate text-[13px] text-slate-900">
                            {c.title}
                          </span>
                          <span className="block truncate text-[11px] text-slate-400">
                            {[c.matter_ref, c.forum, c.client_name].filter(Boolean).join(" · ") ||
                              "No reference"}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
                {selectedCase && (
                  <p className="text-[11px] text-slate-500">
                    {selectedCase.forum || "Forum not set"} · client{" "}
                    <span className="text-slate-800">
                      {selectedCase.client_name || selectedCase.represented_party || "—"}
                    </span>
                  </p>
                )}
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label className="label-caps">Bill to contact person *</Label>
                <SearchPicker
                  items={contactOptions.map((c) => ({
                    id: c.contact_id,
                    label: c.full_name,
                    hint: [c.org, c.onBrief ? "on this brief" : null].filter(Boolean).join(" · "),
                  }))}
                  value={form.contact_id}
                  onSelect={(v) => setForm((f) => ({ ...f, contact_id: v }))}
                  placeholder="Start typing the billing party's name…"
                  testId="builder-contact"
                  emptyLabel="No contact person matches that text."
                />
                {briefContacts.length > 0 && (
                  <p className="text-[11px] text-slate-500" data-testid="builder-recipients-hint">
                    Emailed to all {briefContacts.length} contact person(s) on this matter:{" "}
                    {briefContacts.map((p) => p.full_name).join(", ")}
                  </p>
                )}
              </div>
              </>
              )}
              {mode === "matter" && (
                <div className="space-y-3 sm:col-span-2">
                  <Label className="label-caps">Billed to on the fee note</Label>
                  <SearchPicker
                    items={billToOptions}
                    value={billTo}
                    onSelect={onBillToChange}
                    placeholder="The contact person's own details"
                    testId="builder-billto"
                    emptyLabel="Nothing matches — choose “Type a custom billed-to block”."
                  />
                  {billTo !== "contact" && (
                    <div className="space-y-4 border border-slate-200 p-4">
                      <BilledToFields
                        value={form.billed_to}
                        onChange={setBlockField}
                        testPrefix="billed-to"
                      />
                      {clientId ? (
                        <label className="flex items-center gap-2.5 text-[11px] uppercase tracking-[0.12em] text-slate-500">
                          <Switch
                            checked={!!form.remember_billed_to}
                            onCheckedChange={(v) =>
                              setForm((f) => ({ ...f, remember_billed_to: v }))
                            }
                            data-testid="builder-remember-billed-to"
                          />
                          Remember this for future fee notes for{" "}
                          {selectedCase?.client_name || "this client"}
                        </label>
                      ) : null}
                    </div>
                  )}
                </div>
              )}
              {mode === "adhoc" && (
                <div className="space-y-3 sm:col-span-2">
                  <Label className="label-caps">Billed to *</Label>
                  <div className="border border-slate-200 p-4">
                    <BilledToFields
                      value={form.billed_to}
                      onChange={setBlockField}
                      testPrefix="billed-to"
                    />
                  </div>
                </div>
              )}
              <div className="space-y-2 sm:col-span-2">
                <Label className="label-caps">Show on the fee note</Label>
                <Select
                  value={form.party_display || "both"}
                  onValueChange={(v) => setForm((f) => ({ ...f, party_display: v }))}
                >
                  <SelectTrigger className="rounded-sm" data-testid="builder-party-display">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PARTY_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] leading-relaxed text-slate-500" data-testid="party-hint">
                  {partyHint}
                </p>
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Invoice date</Label>
                <Input
                  type="date"
                  value={form.issue_date}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      issue_date: e.target.value,
                      due_date: addDays(e.target.value, settings?.payment_terms_days ?? 15),
                    }))
                  }
                  className="num rounded-sm"
                  data-testid="builder-issue-date"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Due date</Label>
                <Input
                  type="date"
                  value={form.due_date || ""}
                  onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))}
                  className="num rounded-sm"
                  data-testid="builder-due-date"
                />
              </div>
            </div>
          </section>

          <section className="surface" data-testid="invoice-items-section">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <h2 className="font-serif text-xl text-slate-900">Particulars</h2>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, items: [...f.items, blankItem()] }))}
                  data-testid="add-fee-item-button"
                  className="inline-flex items-center gap-1.5 border border-slate-300 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-700 transition-colors duration-150 hover:bg-slate-50"
                >
                  <Plus className="h-3 w-3" strokeWidth={2.2} />
                  Fee line
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setForm((f) => ({ ...f, items: [...f.items, blankItem("disbursement")] }))
                  }
                  data-testid="add-disbursement-button"
                  className="inline-flex items-center gap-1.5 border border-slate-300 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-700 transition-colors duration-150 hover:bg-slate-50"
                >
                  <Plus className="h-3 w-3" strokeWidth={2.2} />
                  Disbursement
                </button>
              </div>
            </div>

            <div className="divide-y divide-slate-100">
              {form.items.map((item, index) => (
                <div key={item.item_id} className="p-5" data-testid={`invoice-item-${index}`}>
                  <div className="grid gap-3 sm:grid-cols-12">
                    <div className="space-y-1.5 sm:col-span-4">
                      <Label className="label-caps">Type</Label>
                      <Select
                        value={item.kind}
                        onValueChange={(v) =>
                          setItem(index, { kind: v, taxable: v !== "disbursement" })
                        }
                      >
                        <SelectTrigger className="rounded-sm" data-testid={`item-kind-${index}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ITEM_KINDS.map((k) => (
                            <SelectItem key={k.value} value={k.value}>
                              {k.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5 sm:col-span-8">
                      <Label className="label-caps">Particulars</Label>
                      <Input
                        value={item.description}
                        onChange={(e) => setItem(index, { description: e.target.value })}
                        placeholder="Final hearing before Division Bench"
                        className="rounded-sm"
                        data-testid={`item-description-${index}`}
                      />
                    </div>
                    <div className="space-y-1.5 sm:col-span-5">
                      <Label className="label-caps">Detail (hearing date, hours…)</Label>
                      <Input
                        value={item.detail || ""}
                        onChange={(e) => setItem(index, { detail: e.target.value })}
                        className="rounded-sm"
                        data-testid={`item-detail-${index}`}
                      />
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label className="label-caps">Qty / hrs</Label>
                      <Input
                        type="number"
                        step="0.25"
                        min="0"
                        value={item.quantity}
                        onChange={(e) => setItem(index, { quantity: e.target.value })}
                        className="num rounded-sm"
                        data-testid={`item-quantity-${index}`}
                      />
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label className="label-caps">Rate</Label>
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        value={item.rate}
                        onChange={(e) => setItem(index, { rate: e.target.value })}
                        className="num rounded-sm"
                        data-testid={`item-rate-${index}`}
                      />
                    </div>
                    <div className="sm:col-span-3 sm:pt-6">
                      <div className="flex items-center justify-between gap-3">
                        <p
                          className="num text-[13px] text-slate-900"
                          data-testid={`item-amount-${index}`}
                        >
                          {money(
                            Number(item.quantity || 0) * Number(item.rate || 0),
                            currency,
                          )}
                        </p>
                        <button
                          type="button"
                          onClick={() =>
                            setForm((f) => ({
                              ...f,
                              items: f.items.filter((_, i) => i !== index),
                            }))
                          }
                          data-testid={`remove-item-${index}`}
                          className="flex h-8 w-8 items-center justify-center border border-slate-200 text-slate-400 transition-colors duration-150 hover:border-red-300 hover:text-red-600"
                        >
                          <Trash2 className="h-3.5 w-3.5" strokeWidth={1.6} />
                        </button>
                      </div>
                    </div>
                  </div>
                  {taxOn && (
                    <label className="mt-3 flex items-center gap-2.5 text-[11px] uppercase tracking-[0.12em] text-slate-500">
                      <Switch
                        checked={!!item.taxable}
                        onCheckedChange={(v) => setItem(index, { taxable: v })}
                        data-testid={`item-taxable-${index}`}
                      />
                      Charge {taxLabel} on this line
                    </label>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section className="surface p-6">
            <Label className="label-caps">Notes on the invoice</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={3}
              placeholder="Fees as per engagement letter dated…"
              className="mt-2 rounded-sm"
              data-testid="builder-notes-input"
            />
          </section>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-8 lg:self-start" data-testid="builder-summary">
          <div className="surface p-6">
            <h2 className="font-serif text-xl text-slate-900">Statement</h2>
            <dl className="mt-5 space-y-2.5 text-[13px]">
              <div className="flex justify-between text-slate-600">
                <dt>Professional fees</dt>
                <dd className="num text-slate-900">{money(totals.fees, currency)}</dd>
              </div>
              <div className="flex justify-between text-slate-600">
                <dt>Disbursements</dt>
                <dd className="num text-slate-900">{money(totals.disb, currency)}</dd>
              </div>
              <div className="flex justify-between border-t border-slate-100 pt-2.5 text-slate-600">
                <dt>Subtotal</dt>
                <dd className="num text-slate-900">{money(totals.subtotal, currency)}</dd>
              </div>
            </dl>

            <div className={`mt-5 grid gap-3 ${taxOn ? "grid-cols-2" : "grid-cols-1"}`}>
              {taxOn && (
                <div className="space-y-1.5">
                  <Label className="label-caps">{taxLabel} rate %</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={form.tax_rate}
                    onChange={(e) => setForm((f) => ({ ...f, tax_rate: e.target.value }))}
                    className="num rounded-sm"
                    data-testid="builder-tax-rate"
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="label-caps">Discount</Label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={form.discount}
                  onChange={(e) => setForm((f) => ({ ...f, discount: e.target.value }))}
                  className="num rounded-sm"
                  data-testid="builder-discount"
                />
              </div>
            </div>

            <dl className="mt-5 space-y-2.5 border-t border-slate-100 pt-4 text-[13px]">
              {taxOn ? (
                <div className="flex justify-between text-slate-600">
                  <dt>{taxLabel}</dt>
                  <dd className="num text-slate-900">{money(totals.tax, currency)}</dd>
                </div>
              ) : (
                <div className="flex justify-between text-slate-500">
                  <dt>{taxLabel}</dt>
                  <dd className="text-[11px] uppercase tracking-[0.12em]">Not charged</dd>
                </div>
              )}
              <div className="flex items-baseline justify-between border-t border-slate-900 pt-3">
                <dt className="label-caps text-slate-900">Total payable</dt>
                <dd className="num text-lg text-slate-900" data-testid="builder-total">
                  {money(totals.total, currency)}
                </dd>
              </div>
            </dl>
          </div>

          <div className="surface space-y-3 p-6">
            <button
              type="button"
              onClick={() => submit("draft")}
              disabled={saving}
              data-testid="save-draft-button"
              className="inline-flex w-full items-center justify-center gap-2 border border-slate-300 bg-white px-5 py-3 text-[13px] font-semibold text-slate-800 transition-colors duration-150 hover:bg-slate-50 disabled:opacity-60"
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save as draft
            </button>
            <button
              type="button"
              onClick={() => submit("sent")}
              disabled={saving}
              data-testid="save-issue-button"
              className="inline-flex w-full items-center justify-center gap-2 bg-slate-900 px-5 py-3 text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800 disabled:opacity-60"
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save &amp; mark issued
            </button>
            <p className="text-[11px] leading-relaxed text-slate-500">
              Email and WhatsApp dispatch become available on the invoice page once saved.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
